/**
 * React components for css-motion
 */

import React from 'react';
import { useAnimateOnView, useAnimateOnLoad } from './hooks';
import type { AnimationConfig, IntersectionObserverOptions } from '../core';

/**
 * React component that automatically handles animations
 * Wraps children with animation functionality
 *
 * @example
 * ```tsx
 * <AnimateOnView animation={presets.slideUp()}>
 *   <div>This will animate when it comes into view</div>
 * </AnimateOnView>
 * ```
 */
export function AnimateOnView({
  children,
  animation,
  options = {},
  className = '',
  ...props
}: {
  children: React.ReactNode;
  animation?: AnimationConfig;
  options?: IntersectionObserverOptions;
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>) {
  const {
    ref,
    style,
    className: animationClassName,
  } = useAnimateOnView(animation || {}, options);

  return (
    <div
      ref={ref}
      style={style}
      className={`${animationClassName} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * React component for load-triggered animations
 * Animates immediately when the component mounts
 *
 * @example
 * ```tsx
 * <AnimateOnLoad animation={presets.fadeIn()}>
 *   <div>This will animate immediately when loaded</div>
 * </AnimateOnLoad>
 * ```
 */
export function AnimateOnLoad({
  children,
  animation,
  className = '',
  ...props
}: {
  children: React.ReactNode;
  animation: AnimationConfig;
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>) {
  const { style, className: animationClassName } = useAnimateOnLoad(
    animation,
    className
  );

  return (
    <div style={style} className={animationClassName} {...props}>
      {children}
    </div>
  );
}
